import { useEffect, useRef, useState } from 'react';
import waveformUrl from './assets/waveform.webp';
import { BrandMark } from './BrandMark';
import type { JoinPhase, Room, RoomsResponse } from './types';

interface LobbyProps {
  name: string;
  onNameChange: (name: string) => void;
  onJoin: (roomId: string, passphrase?: string) => void;
  onCreate: () => void;
  busy: boolean;
  error: string | null;
  lockedRoomId: string | null;
  passphraseError: string | null;
  phase: JoinPhase;
  onDismissLock: () => void;
}

const POLL_MS = 5000;

function LockIcon() {
  return (
    <svg
      className="room-lock"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="3.25"
        y="7"
        width="9.5"
        height="6.25"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** The room's only status surface — occupancy, lock and join progress all print
 *  here, so no other part of a row can ever change size. */
function Readout({ room, pending }: { room: Room; pending: JoinPhase }) {
  if (pending) return <>{pending === 'connect' ? 'Connecting…' : 'Getting you in…'}</>;
  return (
    <>
      {room.locked ? <LockIcon /> : null}
      {room.occupied ? (
        <>
          <b className="tally">{room.count}</b> in room
        </>
      ) : (
        'Empty'
      )}
      {room.locked ? (
        <>
          <span aria-hidden="true"> · </span>Locked
        </>
      ) : null}
    </>
  );
}

/** Spoken form of the readout, composed onto the row so a screen reader hears the
 *  action, the full (possibly clamped) title, and the room's state as one name. */
function rowLabel(room: Room): string {
  const occ = room.occupied ? `${room.count} in room` : 'empty';
  return room.locked
    ? `Unlock ${room.title}, locked, ${occ}`
    : `Join ${room.title}, ${occ}`;
}

export function Lobby({
  name,
  onNameChange,
  onJoin,
  onCreate,
  busy,
  error,
  lockedRoomId,
  passphraseError,
  phase,
  onDismissLock,
}: LobbyProps) {
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [roomsError, setRoomsError] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState('');
  // Which room the visitor actually pressed. Null while creating, so a failure
  // can be attributed to the row that produced it rather than to the card.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const pendingRef = useRef<string | null>(null);
  const [errorRoomId, setErrorRoomId] = useState<string | null>(null);
  // Locally expanded row. `lockedRoomId` still covers the case where a room was
  // locked between polls and /api/token answered 403 after the press.
  const [openId, setOpenId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  const nameReady = name.trim().length > 0;
  const expandedId = openId ?? lockedRoomId;

  // Poll the room list on mount and every few seconds. The lobby remounts on
  // each return from a meeting (meeting → undefined), so the mount fetch also
  // serves as the post-leave refresh.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    async function load() {
      try {
        const res = await fetch('/api/rooms', { signal: controller.signal });
        if (!res.ok) throw new Error(`Server error (${res.status})`);
        const data = (await res.json()) as RoomsResponse;
        if (!active) return;
        setRooms(data.rooms);
        setRoomsError(null);
      } catch (err) {
        if (active && (err as Error).name !== 'AbortError') {
          // `rooms` is deliberately left alone: a stale list is more useful than
          // an empty one, and the footnote below says it may be out of date.
          setRoomsError('Could not load rooms.');
        }
      }
    }
    void load();
    const id = setInterval(load, POLL_MS);
    return () => {
      active = false;
      controller.abort();
      clearInterval(id);
    };
  }, []);

  // A join settles by `busy` dropping. Bind any error to the row that was
  // pressed before that identity is lost, so the message renders at the point of
  // interaction rather than at the bottom of a list.
  useEffect(() => {
    if (busy) return;
    setErrorRoomId(error ? pendingRef.current : null);
    pendingRef.current = null;
    setPendingId(null);
  }, [busy, error]);

  // A passphrase typed for one room must never be submittable to another. A
  // failed attempt on the *same* room keeps its value, so the retry is the field.
  useEffect(() => {
    setPassphrase('');
  }, [expandedId]);

  useEffect(() => {
    if (passphraseError) passRef.current?.select();
  }, [passphraseError]);

  const focusName = () => nameRef.current?.focus();

  // Nothing on this screen is `disabled` — a control that cannot act yet asks for
  // the one thing it needs instead of going grey and leaving the tab order.
  const guard = (run: () => void) => {
    if (busy) return;
    if (!nameReady) {
      focusName();
      return;
    }
    run();
  };

  const startJoin = (roomId: string, pass?: string) => {
    pendingRef.current = roomId;
    setPendingId(roomId);
    onJoin(roomId, pass);
  };

  const pressRow = (room: Room) =>
    guard(() => {
      // `room.locked` is already known from /api/rooms, so unlocking expands the
      // row locally — no round trip that unlocks nothing and reveals no field.
      if (room.locked) {
        setOpenId(room.id);
        return;
      }
      startJoin(room.id);
    });

  const dismissRow = () => {
    setOpenId(null);
    onDismissLock();
  };

  const occupiedCount = rooms?.filter((r) => r.occupied).length ?? 0;
  const liveLabel =
    !rooms || rooms.length === 0
      ? null
      : occupiedCount > 0
        ? `${occupiedCount} live`
        : `${rooms.length} open`;

  const slotText = !nameReady
    ? 'Needed to join'
    : name.length >= 48
      ? `${name.length}/64`
      : '';

  const creating = busy && pendingId === null;
  // A room that has already dropped out of the poll has no row to hang its
  // failure on, so the message falls back to the card.
  const errorHasRow =
    errorRoomId !== null && Boolean(rooms?.some((r) => r.id === errorRoomId));
  const cardError = error && !errorHasRow ? error : null;

  const loading = rooms === null && !roomsError;
  const listBroken = rooms === null && Boolean(roomsError);
  const empty = rooms !== null && rooms.length === 0;

  return (
    <div className="entry">
      <div className="entry-card lobby-card">
        {/* Ground on surface, like every input and room row below it — the mark
            reads as a recessed plate in the card, not as applied decoration.
            The trace is the one part code cannot fake honestly; the wordmark is
            live text, so it stays crisp at any DPR and fetches no font. */}
        <div className="brand-banner">
          <img
            className="brand-trace"
            src={waveformUrl}
            width={1260}
            height={271}
            alt=""
            decoding="async"
            fetchPriority="high"
          />
          <BrandMark className="brand-mark" />
        </div>
        <p className="eyebrow">
          Stereo Opus to 512 kbps<span aria-hidden="true"> · </span>No third-party
          requests
        </p>
        <h1>Join a room</h1>

        <div className="label-row">
          <label className="micro-label" htmlFor="display-name">
            Your name
          </label>
          {/* Always rendered. Mounting this on the first keystroke shortened the
              card and jumped the whole room list under the pointer. */}
          <span className="label-slot" id="name-slot">
            {slotText}
          </span>
        </div>
        <input
          id="display-name"
          className="name-input"
          ref={nameRef}
          autoFocus
          maxLength={64}
          autoComplete="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="done"
          placeholder="Shown to everyone in the room"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={busy}
          aria-describedby={slotText ? 'name-slot' : undefined}
        />

        <div className="rooms-head">
          <h2 className="micro-label">
            Rooms
            {liveLabel ? (
              <span className="live-slot">
                <span aria-hidden="true"> · </span>
                {liveLabel}
              </span>
            ) : null}
          </h2>
          <button
            className="create-room"
            onClick={() => guard(onCreate)}
            aria-disabled={busy || undefined}
          >
            {creating ? 'Creating…' : 'New room'}
          </button>
        </div>

        {cardError ? (
          <p className="entry-error" role="alert">
            {cardError}
          </p>
        ) : null}

        <div className="room-list">
          {loading ? (
            <div className="list-note is-quiet" role="status" aria-live="polite">
              <p className="line1">Looking for open rooms…</p>
            </div>
          ) : null}

          {listBroken ? (
            <div className="list-note" role="status" aria-live="polite">
              <p className="line1">Can’t reach the room list.</p>
              {/* No retry button: the 5s poll *is* the retry. */}
              <p className="line2">Still trying, every five seconds.</p>
            </div>
          ) : null}

          {empty ? (
            <div className="list-note" role="status" aria-live="polite">
              <p className="line1">No rooms are open.</p>
              <p className="line2">Start one with New room, above.</p>
            </div>
          ) : null}

          {rooms && rooms.length > 0 ? (
            <ul className="room-items">
              {rooms.map((room) => {
                const isOpen = expandedId === room.id;
                const isPending = busy && pendingId === room.id;
                const rowPhase: JoinPhase = isPending ? phase : null;
                const rowError = error && errorRoomId === room.id ? error : null;

                return (
                  <li className="room-item" key={room.id}>
                    {isOpen ? (
                      <div className="room-row is-open">
                        <span
                          className={`room-dot${room.occupied ? ' on' : ''}`}
                          aria-hidden="true"
                        />
                        <span className="room-name" title={room.title}>
                          {room.title}
                        </span>
                        <span className="room-read">
                          <Readout room={room} pending={rowPhase} />
                        </span>
                        <button className="row-cancel" onClick={dismissRow}>
                          Cancel
                        </button>
                        <div className="room-unlock">
                          <label className="micro-label" htmlFor={`pass-${room.id}`}>
                            Passphrase
                          </label>
                          {/* On its own wrapping line rather than in the
                              placeholder, which clips without an ellipsis in a
                              164px field at 320px. */}
                          <p className="unlock-hint" id={`hint-${room.id}`}>
                            Given to you by whoever locked the room.
                          </p>
                          <div className="unlock-line">
                            <input
                              id={`pass-${room.id}`}
                              className="room-pass"
                              ref={passRef}
                              type="password"
                              autoFocus
                              autoComplete="off"
                              enterKeyHint="go"
                              aria-invalid={passphraseError ? true : undefined}
                              aria-describedby={
                                passphraseError
                                  ? `hint-${room.id} err-${room.id}`
                                  : `hint-${room.id}`
                              }
                              value={passphrase}
                              onChange={(e) => setPassphrase(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  guard(() => startJoin(room.id, passphrase));
                                }
                                if (e.key === 'Escape') dismissRow();
                              }}
                            />
                            <button
                              className="action action-btn"
                              onClick={() =>
                                guard(() => startJoin(room.id, passphrase))
                              }
                              aria-disabled={busy || undefined}
                            >
                              Unlock
                            </button>
                          </div>
                          {passphraseError ? (
                            <p
                              className="room-error"
                              id={`err-${room.id}`}
                              role="alert"
                            >
                              That passphrase didn’t match. Check it and try again.
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <button
                        className="room-row"
                        onClick={() => pressRow(room)}
                        aria-disabled={busy || undefined}
                        aria-label={rowLabel(room)}
                      >
                        <span
                          className={`room-dot${room.occupied ? ' on' : ''}`}
                          aria-hidden="true"
                        />
                        <span className="room-name" title={room.title}>
                          {room.title}
                        </span>
                        <span className="room-read">
                          <Readout room={room} pending={rowPhase} />
                        </span>
                        <span className="action action-chip">
                          {room.locked ? 'Unlock' : 'Join'}
                        </span>
                      </button>
                    )}
                    {rowError ? (
                      <p className="row-error" role="alert">
                        {rowError}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        {/* The list kept rendering through a failed poll, so say so rather than
            presenting stale rows as current. */}
        {roomsError && rooms && rooms.length > 0 ? (
          <p className="list-foot">Room list may be out of date — still trying.</p>
        ) : null}

        <span className="sr-only" role="status">
          {busy && phase === 'token' ? 'Getting you in…' : ''}
          {busy && phase === 'connect' ? 'Connecting…' : ''}
        </span>
      </div>
    </div>
  );
}
